import { useQuery } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgSmartAccount } from '~/types'

type PasskeySmartAccountData = Omit<
  OrgSmartAccount,
  | 'signers'
  | 'eoaSigners'
  | 'hasBlockingTransaction'
  | 'isPublicSmartAccount'
  | 'chainIds'
> & {
  orgName: string
}

export const usePasskeySmartAccounts = (passkeyId?: string) => {
  const { serverGetRequest } = useServerRequests()

  const fetchPasskeySmartAccounts = async () => {
    if (!passkeyId) return []

    const result = await serverGetRequest<{
      smartAccounts: PasskeySmartAccountData[]
    }>({
      path: `/api/passkey/${passkeyId}/smart-accounts`,
    })

    if (!result) {
      throw new Error('Failed to fetch smart accounts for passkey')
    }

    return result.data.smartAccounts.map(toOrgSmartAccount)
  }

  const query = useQuery({
    queryKey: [QUERY_KEYS.PASSKEY_SMART_ACCOUNTS, passkeyId],
    queryFn: fetchPasskeySmartAccounts,
    enabled: !!passkeyId,
  })

  return {
    smartAccounts: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// converts server response to OrgSmartAccount so we can use it in useSmartAccountsBalancesMap
const toOrgSmartAccount = (
  account: PasskeySmartAccountData,
): OrgSmartAccount & { orgName: string } => ({
  ...account,
  signers: [],
  eoaSigners: [],
  hasBlockingTransaction: false,
  isPublicSmartAccount: false as const,
  chainIds: [],
})
